import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useCookies } from 'react-cookie'
import { useGoogleLogin } from '@react-oauth/google'
import TagManager from 'react-gtm-module'
import { motion } from 'framer-motion'

import InputField from '../../components/InputField'
import { selectToken } from '../../containers/authentication/authenticationSlice'
import useSignUp from '../../containers/user/useSignUp'
import TermsAndConditions from './TermsAndConditions'
import useStorage from 'containers/useStorage'
import Modal from '../../components/Modal'

import mayaLogo from 'images/maya-logo.png'

import './SignUp.scss'
import useQuery from 'containers/useQuery'
import usePageTitle from 'containers/pageTitle/usePageTitle'
import ScreenLoading from 'components/ScreenLoading'
import useMicrosoft from 'containers/authentication/useMicrosoft'
import { trackSignupCompleted } from 'utils/signupTracking'
import { getSignupAttribution } from 'utils/signupAttribution'

const SignUp = () => {
  usePageTitle('Sign Up')

  const token = useSelector(selectToken)
  const [cookies] = useCookies(['user_id'])
  const query = useQuery()
  const storage = useStorage()
  let history = useHistory()
  const location = useLocation()

  const [showEmailForm, setShowEmailForm] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [loadingEmailSignUp, setLoadingEmailSignUp] = useState(false)
  const [credentials, setCredentials] = useState({
    email: query.get('email') || '',
    password: '',
    acceptTerms: false,
  })

  const [signingUpWithGoogle, setSigningUpWithGoogle] = useState(false)
  const [signingUpWithMicrosoft, setSigningUpWithMicrosoft] = useState(false)
  const [googleAccessToken, setGoogleAccessToken] = useState()
  const [microsoftAccessToken, setMicrosoftAccessToken] = useState()

  const {
    signUpError,
    signUpWithGoogle,
    signUpWithMicrosoft,
    removeError,
    removeAllErrors,
    validateCredentials,
    signUp,
  } = useSignUp()

  const { getAuthUri } = useMicrosoft()

  const setCredential = (key, value) => {
    setCredentials((prev) => ({ ...prev, [key]: value }))
  }

  const handleClickCheck = () => {
    if (signUpError.acceptTerms) {
      removeError('acceptTerms')
    }
    setCredential('acceptTerms', !credentials.acceptTerms)
  }

  // Microsoft OAuth
  const handleSignUpWithMicrosoft = async () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'sign_up_microsoft_clicked',
        is_custom_track: true,
      },
    })

    const response = await getAuthUri()
    if (response) {
      window.location.href = response.auth_uri
    }
  }

  // /microsoft/redirect hands over the token of a Microsoft account with no Maya user yet
  useEffect(() => {
    const token = location.state?.microsoftAccessToken
    if (token) {
      // Drop it from history so a refresh doesn't replay the signup
      history.replace({ ...location, state: undefined })
      setMicrosoftAccessToken(token)
      setSigningUpWithMicrosoft(true)
    }
  }, [location.state])

  // Execute signup after OAuth tokens are set
  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      setGoogleAccessToken(tokenResponse.access_token)
      setSigningUpWithGoogle(true)
      TagManager.dataLayer({
        dataLayer: {
          event: 'sign_up_google_clicked',
          is_custom_track: true,
        },
      })
    },
    onError: () => {
      console.log('Google sign up failed')
    },
  })

  useEffect(() => {
    if (signingUpWithGoogle && googleAccessToken) {
      handleContinue()
    }
  }, [signingUpWithGoogle, googleAccessToken])

  useEffect(() => {
    if (signingUpWithMicrosoft && microsoftAccessToken) {
      handleContinue()
    }
  }, [signingUpWithMicrosoft, microsoftAccessToken])

  const handleContinue = async (event) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    let res = null

    if (signingUpWithGoogle) {
      res = await signUpWithGoogle(credentials, googleAccessToken)
    } else if (signingUpWithMicrosoft) {
      res = await signUpWithMicrosoft(credentials, microsoftAccessToken)
      if (!res) {
        // Leave the loading screen so the signup error is visible
        setSigningUpWithMicrosoft(false)
        setMicrosoftAccessToken(undefined)
      }
    } else {
      // Email/password signup - validate first
      setLoadingEmailSignUp(true)
      let validationPassed = false
      validateCredentials(credentials, async () => {
        validationPassed = true
        res = await signUp(credentials)

        if (res) {
          trackSignupCompleted(res.data, 'email')
        }
        setLoadingEmailSignUp(false)
      })
      // If validation failed, it won't call the callback, so stop loading
      if (!validationPassed) {
        setLoadingEmailSignUp(false)
      }
    }

    // Email signups are tracked inside the validation callback above; this covers the
    // social ones, whose response is the user record itself.
    if (res && (signingUpWithGoogle || signingUpWithMicrosoft)) {
      trackSignupCompleted(res, signingUpWithGoogle ? 'google' : 'microsoft')
    }
  }

  // Redirect if already logged in
  if (token && cookies.user_id) {
    // Honor return_to first (threaded through from /sign-in?return_to=…, e.g. the
    // Shopify mid-install hop): a brand-new user signing up mid-install must land
    // back on /shopify-integration — going straight to '/' skips the flow that
    // authorizes the post-org-creation resume (#1645), silently abandoning the
    // store connect. Relative paths only: return_to is caller-controlled and must
    // not become an open redirect.
    const returnTo = query.get('return_to')
    if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      window.location.href = returnTo
      return null
    }
    // Redirect to home, which will handle organization routing
    window.location.href = storage.redirectTo || '/'
    return null
  }

  // Show loading screen during OAuth signup
  if (signingUpWithGoogle || signingUpWithMicrosoft) {
    return <ScreenLoading />
  }

  // Keep the query string (return_to, intent, shop) so a merchant mid Shopify install who
  // already has an account resumes the install after signing in.
  const redirectToSignIn = () => {
    history.push({ pathname: '/sign-in', search: location.search })
  }

  // Arrivals from "Start with your store" (www, the blog) and from the Shopify App Store
  // get Shopify copy (#1818). Same form, same flow.
  const attribution = getSignupAttribution()
  const shopifyIntent =
    query.get('intent') === 'shopify' ||
    !!query.get('shop') ||
    (query.get('return_to') || '').startsWith('/shopify-integration')
  const shopName = (query.get('shop') || attribution.shop || '').replace(/\.myshopify\.com$/i, '')

  return (
    <div className="page-column">
      <header>
        <div className="logo">
          <img src={mayaLogo} alt="Maya" />
        </div>
      </header>
      <div className="container-grow">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="container-column sign-up-box"
        >
          <div className="title-container">
            <h1 className="text-align-center">
              {shopifyIntent ? 'Connect your Shopify store' : 'Sign Up'}
            </h1>
            {shopifyIntent && (
              <div className="secondary-text text-align-center">
                {shopName
                  ? `Create your Maya account and we'll connect ${shopName} next.`
                  : "Create your Maya account, then connect your store in one step."}
              </div>
            )}
            <div className="secondary-text text-align-center">
              Already have an account?{' '}
              <button className="link" onClick={redirectToSignIn}>
                Sign in
              </button>
            </div>
          </div>
          <form
            onSubmit={handleContinue}
            className="container-grow container-column sign-up"
            noValidate
          >
            {/* OAuth Buttons */}
            <div onClick={googleLogin} className="with-google" style={{marginBottom: "10px"}}>
              Sign up with Google
            </div>
            <div onClick={handleSignUpWithMicrosoft} className="with-microsoft">
              Sign up with Microsoft
            </div>

            {/* Email Sign-Up Button - Only show if email form is not visible */}
            {!showEmailForm && (
              <div
                onClick={() => {
                  setShowEmailForm(true)
                  TagManager.dataLayer({
                    dataLayer: {
                      event: 'sign_up_email_clicked',
                      is_custom_track: true,
                    },
                  })
                }}
                className="with-email"
              >
                Sign up with email
              </div>
            )}

            {/* Email Form - Shows when button clicked */}
            {showEmailForm && (
              <>
                <div className="or">OR</div>
                <div className="container-column width-100 credentials">
                  <InputField
                    value={credentials.email}
                    setValue={(value) => setCredential('email', value)}
                    label="Email"
                    type="email"
                    icon="email"
                    placeholder="you@example.com"
                    error={signUpError.email}
                    removeError={() => removeError('email')}
                    required
                  />
                  <InputField
                    value={credentials.password}
                    setValue={(value) => setCredential('password', value)}
                    label="Password"
                    type="password"
                    icon="lock"
                    placeholder="Create a strong password"
                    error={signUpError.password}
                    removeError={() => removeError('password')}
                    helperText="Must be at least 8 characters"
                    required
                  />
                </div>

                {/* Terms & Conditions Link */}
                <div className="terms-link-section">
                  <div
                    onClick={handleClickCheck}
                    className={`checkbox-input ${signUpError.acceptTerms ? 'error' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={credentials.acceptTerms}
                      readOnly
                      data-lpignore="true"
                    />
                    <label>
                      I agree to the{' '}
                      <button
                        type="button"
                        className="terms-link"
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowTermsModal(true)
                        }}
                      >
                        terms and conditions
                      </button>
                    </label>
                  </div>
                  {signUpError.acceptTerms && (
                    <span className="error-message">{signUpError.acceptTerms}</span>
                  )}
                </div>
              </>
            )}

            {/* Server Error */}
            {signUpError.server && (
              <span className="server-error">{signUpError.server}</span>
            )}

            {/* Submit Button - Only show if email form is visible */}
            {showEmailForm && (
              <button type="submit" className="submit-button" disabled={loadingEmailSignUp}>
                {loadingEmailSignUp ? (
                  <ScreenLoading spinnerColor="white" />
                ) : (
                  'Sign Up'
                )}
              </button>
            )}
          </form>
        </motion.div>
      </div>

      {/* Terms and Conditions Modal */}
      <Modal
        show={showTermsModal}
        onHide={() => setShowTermsModal(false)}
        className="terms-modal"
      >
        <div className="modal-content">
          <h2>Terms and Conditions</h2>
          <div className="terms-content">
            <TermsAndConditions />
          </div>
          <div className="modal-actions">
            <button
              className="accept-button"
              onClick={() => {
                setCredential('acceptTerms', true)
                setShowTermsModal(false)
                if (signUpError.acceptTerms) {
                  removeError('acceptTerms')
                }
              }}
            >
              Accept
            </button>
            <button
              className="close-button-text"
              onClick={() => setShowTermsModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default SignUp
